/* Copyright (c) 2024 Xperi. All Rights Reserved. */
import {
  SessionErrorResponse,
  SessionErrorResponseCodeEnum
} from "client-code/session-manager";

import { RestrictionErrorCode, SessionErrorCode } from "./PlaybackErrorCodes";
import { LocaleKeys } from "../../locale/LocaleKeys";
import { Log } from "../../logging";
import { PlaybackError } from "./PlaybackError";
import { Locale } from "../../locale";
import { BrandingUiInfoKeys } from "../../entities";
import { Branding } from "../../branding";
import { PlaybackContentType } from "../enums/PlaybackContentType";

/*
    All the playback session realted error from v1/sessionCreate are mapped as a part of
    SessionErrorMapper.
    Example: AuthenticationExpired, NotAuthorizedOutOfHome, TooManyStreams etc.
*/

export class SessionErrorMapper {
  private static TAG: string = "SessionErrorMapper";

  private code: string;

  private description: string;

  private message: string;

  private title: string;

  private type: string;

  public constructor() {
    this.code = "";
    this.description = "";
    this.message = "";
    this.title = "";
    this.type = "";
  }

  private getTooManyStreamsForResourceMessage(): string {
    return Branding.applyCustomerSupportPlaceholders(
      Locale.getString(
        LocaleKeys.SESSION_ERROR_TOO_MANY_STREAMS_FOR_RESOURCE_MESSAGE
      )
    );
  }

  private getSessionCommonPlaybackErrorMessage(): string {
    return Locale.getString(LocaleKeys.SESSION_COMMON_PLAYBACK_ERROR).replace(
      "{{deviceNameShort}}",
      Branding.getFallbackBrandingUiInfoForKey(
        BrandingUiInfoKeys.DEVICE_NAME_SHORT
      ) as string
    );
  }

  private readonly genericErrorCode = {
    LIVE: "V851",
    VOD: "V852",
    STARTOVER_BEGINNING: "V853",
    STARTOVER_RESUME: "V853",
    CATCHUP_BEGINNING: "V853",
    CATCHUP_RESUME: "V853",
    NDVR_LIVE: "V854",
    NDVR_RECORDED: "V854"
  };

  private readonly genericErrorDetails: Partial<
    Record<SessionErrorResponseCodeEnum, { subErrorCode: string; type: string }>
  > = {
    badConfiguration: {
      subErrorCode: "N604",
      type: "SESSION_BAD_CONFIGURATION_ERROR"
    },
    badArgument: { subErrorCode: "N604", type: "SESSION_BAD_ARGUMENT_ERROR" },
    externalError: { subErrorCode: "S605", type: "SESSION_EXTERNAL_ERROR" },
    deprecated: { subErrorCode: "S616", type: "SESSION_DEPRECATED_ERROR" },
    hostUnavailable: {
      subErrorCode: "N601",
      type: "SESSION_HOST_UNAVAILABLE_ERROR"
    }
  };

  private setSessionErrorPropertiesByCode(
    errorCode: SessionErrorResponseCodeEnum,
    playbackContentType: PlaybackContentType
  ) {
    switch (errorCode) {
      case SessionErrorResponseCodeEnum.AuthenticationExpired:
        this.type = "SESSION_AUTHENTICATION_EXPIRED_ERROR";
        this.title = Locale.getString(
          LocaleKeys.LIVE_TV_APP_SESSION_EXPIRED_TITLE
        );
        this.message = Locale.getString(
          LocaleKeys.LIVE_TV_APP_SESSION_EXPIRED_MESSAGE
        ).replaceAll(
          "{{displayName}}",
          Branding.getFallbackBrandingUiInfoForKey(
            BrandingUiInfoKeys.DISPLAY_NAME
          ) as string
        );
        this.code = SessionErrorCode.AUTHENTICATION_EXPIRED;
        break;
      case SessionErrorResponseCodeEnum.BadArgument:
      case SessionErrorResponseCodeEnum.BadConfiguration:
      case SessionErrorResponseCodeEnum.Deprecated:
      case SessionErrorResponseCodeEnum.ExternalError:
      case SessionErrorResponseCodeEnum.HostUnavailable: {
        this.title = Locale.getString(LocaleKeys.PLAYER_ERROR_CANT_PLAY_TITLE);
        const errorDetails = this.genericErrorDetails[errorCode];
        this.type = (errorDetails && errorDetails.type) || "";
        this.code = `${this.genericErrorCode[playbackContentType]}-${
          errorDetails && errorDetails.subErrorCode
        }`;
        this.message = this.getSessionCommonPlaybackErrorMessage();
        break;
      }
      case SessionErrorResponseCodeEnum.InternalError:
        this.type = "SESSION_INTERNAL_ERROR";
        this.title = Locale.getString(LocaleKeys.PLAYER_ERROR_CANT_PLAY_TITLE);
        this.message = this.getSessionCommonPlaybackErrorMessage();
        this.code = SessionErrorCode.INTERNAL_ERROR;
        break;
      case SessionErrorResponseCodeEnum.NoSession:
        this.type = "SESSION_NO_SESSION_ERROR";
        this.title = Locale.getString(LocaleKeys.PLAYER_ERROR_CANT_PLAY_TITLE);
        this.message = Locale.getString(
          LocaleKeys.PLAYBACK_ERROR_NO_SESSION_MESSAGE
        ).replace(
          "{{deviceNameShort}}",
          Branding.getFallbackBrandingUiInfoForKey(
            BrandingUiInfoKeys.DEVICE_NAME_SHORT
          ) as string
        );
        this.code = SessionErrorCode.NO_SESSION;
        break;
      case SessionErrorResponseCodeEnum.NotAuthenticated:
        this.type = "SESSION_NOT_AUTHENTICATED_ERROR";
        this.title = Locale.getString(
          LocaleKeys.PLAYER_SOMETHING_WENT_WRONG_TITLE
        );
        this.message = Locale.getString(
          LocaleKeys.PLAYER_SOMETHING_WENT_WRONG_MESSAGE
        ).replace(
          "{{displayName}}",
          Branding.getFallbackBrandingUiInfoForKey(
            BrandingUiInfoKeys.DISPLAY_NAME
          ) as string
        );
        this.code = SessionErrorCode.NOT_AUTHENTICATED;
        break;
      case SessionErrorResponseCodeEnum.NotAuthorized:
        this.type = "SESSION_NOT_AUTHORIZED_ERROR";
        this.title = Locale.getString(
          LocaleKeys.LIVE_TV_SUBSCRIPTION_REQUIRED_TITLE
        );
        this.message = Locale.getString(
          LocaleKeys.SESSION_ERROR_NOT_AUTHORIZED_MESSAGE
        );
        this.code = SessionErrorCode.NOT_AUTHORIZED;
        break;
      case SessionErrorResponseCodeEnum.NotAuthorizedDeviceType:
        this.type = "SESSION_NOT_AUTHORIZED_DEVICE_TYPE_ERROR";
        this.title = Locale.getString(
          LocaleKeys.LIVE_TV_CANT_WATCH_SHOWS_MESSAGE
        );
        this.code = RestrictionErrorCode.DEVICE_RESTRICTED;
        this.message = Locale.getString(LocaleKeys.PLAYBACK_PROHIBITED_MESSAGE);
        break;
      case SessionErrorResponseCodeEnum.NotAuthorizedOutOfHome:
        this.type = "SESSION_NOT_AUTHORIZED_OUT_OF_HOME_ERROR";
        this.title = Locale.getString(LocaleKeys.PLAYBACK_CANT_STREAM_SHOW);
        this.code = RestrictionErrorCode.OUT_OF_HOME;
        this.message = Locale.getString(LocaleKeys.PLAYER_OUT_OF_HOME_MESSAGE);
        break;
      case SessionErrorResponseCodeEnum.NotAuthorizedOutOfRegion:
        this.type = "SESSION_NOT_AUTHORIZED_OUT_OF_REGION_ERROR";
        this.title = Locale.getString(LocaleKeys.PLAYBACK_CANT_STREAM_SHOW);
        this.code = RestrictionErrorCode.OUT_OF_REGION;
        this.message = Locale.getString(
          LocaleKeys.PLAYER_OUT_OF_REGION_MESSAGE
        );
        break;
      case SessionErrorResponseCodeEnum.NotRegistered:
        this.type = "SESSION_NOT_REGISTERED_ERROR";
        this.title = Locale.getString(LocaleKeys.PLAYBACK_CANT_WATCH_SHOWS);
        this.message = Locale.getString(
          LocaleKeys.LIVE_TV_CANT_WATCH_SHOWS_MESSAGE
        ).replace(
          "{{distributorName}}",
          Branding.getFallbackBrandingUiInfoForKey(
            BrandingUiInfoKeys.DISTRIBUTOR_NAME
          ) as string
        );
        this.code = SessionErrorCode.NOT_REGISTERED;
        break;
      case SessionErrorResponseCodeEnum.TooManyStreams:
        this.type = "SESSION_TOO_MANY_STREAMS_ERROR";
        this.title = Locale.getString(LocaleKeys.PLAYBACK_CANT_WATCH_NOW);
        this.message = Locale.getString(
          LocaleKeys.SESSION_ERROR_TOO_MANY_STREAMS_MESSAGE
        );
        this.code = SessionErrorCode.TOO_MANY_STREAMS;
        break;
      case SessionErrorResponseCodeEnum.TooManyStreamsForResource:
        this.type = "SESSION_TOO_MANY_STREAMS_FOR_RESOURCE_ERROR";
        this.title = Locale.getString(LocaleKeys.PLAYBACK_CANT_WATCH_NOW);
        this.message = this.getTooManyStreamsForResourceMessage();
        this.code = SessionErrorCode.TOO_MANY_STREAMS_FOR_RESOURCE;
        break;
      case SessionErrorResponseCodeEnum.UnknownLocation:
        this.type = "SESSION_UNKNOWN_LOCATION_ERROR";
        this.title = Locale.getString(LocaleKeys.PLAYBACK_CANT_STREAM_SHOW);
        this.code = RestrictionErrorCode.LOCATION_RESTRICTION;
        this.message = Locale.getString(
          LocaleKeys.PLAYBACK_UNKNOWN_LOCATION_ERROR
        );
        break;
      default:
        this.type = "SESSION_UNKNOWN_ERROR";
        this.title = Locale.getString(LocaleKeys.LIVE_TV_PLAYER_ERROR_TITLE);
        this.message = Locale.getString(
          LocaleKeys.LIVE_TV_UNABLE_TO_PLAY_MESSAGE
        );

        this.code = SessionErrorCode.UNKNOWN_ERROR;
        break;
    }
  }

  private setSessionErrorPropertiesByError(
    error: SessionErrorResponse,
    playbackContentType: PlaybackContentType
  ) {
    Log.e(SessionErrorMapper.TAG, "session error ", error);
    const errorCode = error.code || "";
    this.message = error.message || "";
    this.setSessionErrorPropertiesByCode(errorCode, playbackContentType);
  }

  public getErrorByCode(
    errorCode: SessionErrorResponseCodeEnum,
    playbackContentType: PlaybackContentType = PlaybackContentType.LIVE
  ): PlaybackError {
    Log.e(SessionErrorMapper.TAG, "session error code ", errorCode);
    this.setSessionErrorPropertiesByCode(errorCode, playbackContentType);
    return new PlaybackError(
      this.code,
      this.message,
      this.title,
      this.description
    );
  }

  public getErrorByErrorObject(
    error: SessionErrorResponse,
    playbackContentType: PlaybackContentType = PlaybackContentType.LIVE
  ): PlaybackError {
    this.setSessionErrorPropertiesByError(error, playbackContentType);
    return new PlaybackError(
      this.code,
      this.message,
      this.title,
      this.description
    );
  }
}
